@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_cus

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.enginetabular.TabularScreen
import org.example.project.service.InfrastrukturApi
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

private val infraBausteinHeaders = listOf("Lagerplatz", "Standort", "Halle", "Regal", "Fach", "Ebene", "Tor", "Bereich")
private val infraTypHeaders = listOf("Bezeichnung", "Typ", "Modultyp", "Standort", "Halle", "Regal", "Fach", "Ebene", "Tor", "Bereich")
private val infraCodeHeaders = listOf("Bezeichnung", "Erzeugter Code", "Anzeige Code", "Klartext")
private val infraTypToggleHeaders = setOf("Modultyp", "Standort", "Halle", "Regal", "Fach", "Ebene", "Tor", "Bereich")

private val infraDefaultFormate = listOf(
    All(
        id = "zahlenformat",
        bezeichnung = "Zahlenformat",
        standort = "SS",
        halle = "HH",
        regal = "RR",
        fach = "FFF",
        ebene = "EE",
        infraTorFormat = "TTT",
        bereich = "BB"
    )
)

private val infraDefaultTypen = emptyList<All>()

private val infraDefault = All(
    id = "infra",
    typ = "INFRA_META",
    bezeichnung = "Infrastruktur",
    standort = infraDefaultFormate.first().standort,
    halle = infraDefaultFormate.first().halle,
    regal = infraDefaultFormate.first().regal,
    fach = infraDefaultFormate.first().fach,
    ebene = infraDefaultFormate.first().ebene,
    infraTorFormat = infraDefaultFormate.first().infraTorFormat,
    bereich = infraDefaultFormate.first().bereich,
    module = infraDefaultTypen,
    columnSettings = emptyList()
)

private fun infraTypUses(row: All, field: String): Boolean =
    when (field) {
        "standort" -> row.standort.isNotBlank()
        "halle" -> row.halle.isNotBlank()
        "regal" -> !row.typ.equals("Tor", ignoreCase = true) && row.regal.isNotBlank()
        "fach" -> !row.typ.equals("Tor", ignoreCase = true) && row.fach.isNotBlank()
        "ebene" -> !row.typ.equals("Tor", ignoreCase = true) && row.ebene.isNotBlank()
        "tor" -> row.infraTorFormat.isNotBlank()
        "bereich" -> !row.typ.equals("Tor", ignoreCase = true) && row.bereich.isNotBlank()
        else -> false
    }

private fun infraTypSet(row: All, field: String, checked: Boolean): All {
    fun value(label: String) = if (checked) label else ""
    if (row.typ.equals("Tor", ignoreCase = true) && field !in setOf("standort", "halle", "tor")) {
        return when (field) {
            "regal" -> row.copy(regal = "")
            "fach" -> row.copy(fach = "")
            "ebene" -> row.copy(ebene = "")
            "bereich" -> row.copy(bereich = "")
            else -> row
        }
    }
    return when (field) {
        "standort" -> row.copy(standort = value("Standort"))
        "halle" -> row.copy(halle = value("Halle"))
        "regal" -> row.copy(regal = value("Regal"))
        "fach" -> row.copy(fach = value("Fach"))
        "ebene" -> row.copy(ebene = value("Ebene"))
        "tor" -> row.copy(infraTorFormat = value("Tor"))
        "bereich" -> row.copy(bereich = value("Bereich"))
        else -> row
    }
}

private fun infraModulTypAktiv(row: All): Boolean =
    row.modulTyp.equals("true", ignoreCase = true) || row.modulTyp == "✅"

private fun infraFormatByField(formate: List<All>): Map<String, String> {
    val row = formate.firstOrNull() ?: All()
    return mapOf(
        "standort" to row.standort,
        "halle" to row.halle,
        "regal" to row.regal,
        "fach" to row.fach,
        "ebene" to row.ebene,
        "tor" to row.infraTorFormat,
        "bereich" to row.bereich
    )
}

private fun infraActiveFields(row: All): List<String> =
    listOf("standort", "halle", "regal", "fach", "ebene", "tor", "bereich")
        .filter { infraTypUses(row, it) }

private fun infraGeneratedCode(row: All, formate: List<All>): String {
    val formats = infraFormatByField(formate)
    return infraActiveFields(row)
        .map { formats[it].orEmpty() }
        .joinToString("")
}

private fun infraAnzeigeCode(row: All, formate: List<All>): String {
    val formats = infraFormatByField(formate)
    val visibleParts = infraActiveFields(row)
        .filterNot { it == "standort" }
        .map { formats[it].orEmpty() }
        .filter { it.isNotBlank() }
    val prefix = when {
        row.typ.startsWith("WA", ignoreCase = true) -> "WA"
        row.typ.equals("Tor", ignoreCase = true) -> "T"
        else -> ""
    }
    return (listOf(prefix).filter { it.isNotBlank() } + visibleParts).joinToString("-")
}

private fun infraKlartext(row: All, formate: List<All>): String {
    fun sample(format: String): String =
        "1".padStart(format.length.coerceAtLeast(1), '0')

    val formats = infraFormatByField(formate)
    val visibleParts = infraActiveFields(row)
        .filterNot { it == "standort" }
        .map { sample(formats[it].orEmpty()) }
    return when {
        row.typ.startsWith("WA", ignoreCase = true) && row.typ.contains("Tor", ignoreCase = true) -> "WA-T1"
        row.typ.equals("Tor", ignoreCase = true) -> "T1"
        row.typ.startsWith("WA", ignoreCase = true) -> (listOf("WA") + visibleParts).joinToString("-")
        else -> visibleParts.joinToString("-")
    }
}

private fun infraCodesFrom(typen: List<All>, formate: List<All>): List<All> =
    typen.map { row ->
        All(
            id = row.id,
            typ = row.typ,
            bezeichnung = row.bezeichnung,
            modulTyp = if (infraModulTypAktiv(row)) "true" else "false",
            infraErzeugterCode = infraGeneratedCode(row, formate),
            infraAnzeigeCode = infraAnzeigeCode(row, formate),
            infraKlartext = infraKlartext(row, formate)
        )
    }

private fun infraFromState(formate: List<All>, typen: List<All>): All {
    val format = formate.firstOrNull() ?: infraDefaultFormate.first()
    return infraDefault.copy(
        standort = format.standort,
        halle = format.halle,
        regal = format.regal,
        fach = format.fach,
        ebene = format.ebene,
        infraTorFormat = format.infraTorFormat,
        bereich = format.bereich,
        module = typen,
        columnSettings = infraCodesFrom(typen, formate)
    )
}

private fun infraFormateFrom(infra: All): List<All> =
    listOf(
        All(
            id = "zahlenformat",
            bezeichnung = "Zahlenformat",
            standort = infra.standort.ifBlank { infraDefaultFormate.first().standort },
            halle = infra.halle.ifBlank { infraDefaultFormate.first().halle },
            regal = infra.regal.ifBlank { infraDefaultFormate.first().regal },
            fach = infra.fach.ifBlank { infraDefaultFormate.first().fach },
            ebene = infra.ebene.ifBlank { infraDefaultFormate.first().ebene },
            infraTorFormat = infra.infraTorFormat.ifBlank { infraDefaultFormate.first().infraTorFormat },
            bereich = infra.bereich.ifBlank { infraDefaultFormate.first().bereich }
        )
    )

@Composable
fun CUSInfrastrukturverwaltung() {
    val scope = rememberCoroutineScope()
    var formate by remember { mutableStateOf(infraDefaultFormate) }
    var typen by remember { mutableStateOf(infraDefaultTypen) }
    var modultypenAktiv by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        modultypenAktiv = loadModultypenFeatureAktiv()
        val loaded = InfrastrukturApi.loadInfra()
        if (loaded != null && loaded.id.isNotBlank()) {
            formate = infraFormateFrom(loaded)
            typen = loaded.module
        }
    }
    val sichtbareInfraTypHeaders = if (modultypenAktiv) {
        infraTypHeaders
    } else {
        infraTypHeaders.filterNot { it == "Modultyp" }
    }
    val sichtbareInfraTypToggleHeaders = if (modultypenAktiv) {
        infraTypToggleHeaders
    } else {
        infraTypToggleHeaders - "Modultyp"
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            padding(12.px)
            height(100.percent)
            overflow("hidden")
            property("box-sizing", "border-box")
            property("min-height", "0")
        }
    }) {
        Div({
            style {
                property("flex", "1 1 0")
                property("min-height", "0")
                overflow("hidden")
            }
        }) {
            TabularScreen(
                screenKey = "InfrastrukturverwaltungFormate",
                rollenKey = "Infrastrukturverwaltung",
                title = "Infrastruktur Formate",
                headers = infraBausteinHeaders,
                models = formate,
                data = formate.map { row ->
                    row.id to listOf(
                        row.bezeichnung,
                        row.standort,
                        row.halle,
                        row.regal,
                        row.fach,
                        row.ebene,
                        row.infraTorFormat,
                        row.bereich
                    )
                },
                editable = true,
                allowCreate = false,
                allowDelete = false,
                allowImportButton = false,
                allowExportButton = false,
                allowSettingsButton = false,
                nonEditableCols = setOf("Lagerplatz"),
                onCommit = { rows ->
                    val nextFormate = rows.map { (id, cols) ->
                        All(
                            id = id,
                            bezeichnung = cols.getOrElse(0) { "Zahlenformat" }.ifBlank { "Zahlenformat" },
                            standort = cols.getOrElse(1) { "" },
                            halle = cols.getOrElse(2) { "" },
                            regal = cols.getOrElse(3) { "" },
                            fach = cols.getOrElse(4) { "" },
                            ebene = cols.getOrElse(5) { "" },
                            infraTorFormat = cols.getOrElse(6) { "" },
                            bereich = cols.getOrElse(7) { "" }
                        )
                    }
                    formate = nextFormate
                    scope.launch {
                        InfrastrukturApi.saveInfra(infraFromState(nextFormate, typen))
                    }
                }
            )
        }

        Div({
            style {
                property("flex", "1 1 0")
                property("min-height", "0")
                overflow("hidden")
            }
        }) {
            TabularScreen(
                screenKey = "InfrastrukturverwaltungTypen",
                rollenKey = "Infrastrukturverwaltung",
                title = "Platz Typen",
                headers = sichtbareInfraTypHeaders,
                models = typen,
                data = typen.map { row ->
                    row.id to buildList {
                        add(row.bezeichnung)
                        add(row.typ)
                        if (modultypenAktiv) add(if (infraModulTypAktiv(row)) "✅" else "⬜")
                        add(if (infraTypUses(row, "standort")) "✅" else "⬜")
                        add(if (infraTypUses(row, "halle")) "✅" else "⬜")
                        add(if (infraTypUses(row, "regal")) "✅" else "⬜")
                        add(if (infraTypUses(row, "fach")) "✅" else "⬜")
                        add(if (infraTypUses(row, "ebene")) "✅" else "⬜")
                        add(if (infraTypUses(row, "tor")) "✅" else "⬜")
                        add(if (infraTypUses(row, "bereich")) "✅" else "⬜")
                    }
                },
                editable = true,
                allowImportButton = false,
                allowExportButton = false,
                allowSettingsButton = false,
                nonEditableCols = emptySet(),
                toggleColumns = sichtbareInfraTypToggleHeaders,
                dropdownOptions = mapOf("Typ" to listOf("Platz", "Tor", "WAPlatz")),
                createRow = { _ ->
                    buildList {
                        add("")
                        add("Platz")
                        if (modultypenAktiv) add("⬜")
                        add("⬜")
                        add("⬜")
                        add("⬜")
                        add("⬜")
                        add("⬜")
                        add("⬜")
                        add("⬜")
                    }
                },
                onCommit = { rows ->
                    var nextId = rows
                        .mapNotNull { it.first.toIntOrNull() }
                        .maxOrNull()
                        ?.plus(1)
                        ?: 1
                    val nextTypen = rows.mapIndexed { index, row ->
                        val id = row.first.takeIf { it.toIntOrNull() != null } ?: (nextId++).toString()
                        val cols = row.second
                        val fieldOffset = if (modultypenAktiv) 1 else 0
                        val existing = typen.firstOrNull { it.id == id } ?: typen.getOrNull(index)
                        var next = All(
                            id = id,
                            bezeichnung = cols.getOrElse(0) { "" },
                            typ = cols.getOrElse(1) { "" },
                            modulTyp = if (modultypenAktiv) {
                                if (cols.getOrElse(2) { "⬜" } == "✅") "true" else "false"
                            } else {
                                existing?.modulTyp.orEmpty()
                            }
                        )
                        listOf(
                            "standort" to 2 + fieldOffset,
                            "halle" to 3 + fieldOffset,
                            "regal" to 4 + fieldOffset,
                            "fach" to 5 + fieldOffset,
                            "ebene" to 6 + fieldOffset,
                            "tor" to 7 + fieldOffset,
                            "bereich" to 8 + fieldOffset
                        ).forEach { (field, colIndex) ->
                            next = infraTypSet(next, field, cols.getOrElse(colIndex) { "⬜" } == "✅")
                        }
                        next
                    }
                    typen = nextTypen
                    scope.launch {
                        InfrastrukturApi.saveInfra(infraFromState(formate, nextTypen))
                    }
                },
                onDeleteRows = { ids ->
                    val nextTypen = typen.filterNot { it.id in ids }
                    typen = nextTypen
                    InfrastrukturApi.saveInfra(infraFromState(formate, nextTypen))
                }
            )
        }

        Div({
            style {
                property("flex", "1 1 0")
                property("min-height", "0")
                overflow("hidden")
            }
        }) {
            TabularScreen(
                screenKey = "InfrastrukturverwaltungCodes",
                rollenKey = "Infrastrukturverwaltung",
                title = "Infrastruktur Muster",
                headers = infraCodeHeaders,
                models = typen,
                data = typen.map { row ->
                    row.id to listOf(
                        row.bezeichnung,
                        infraGeneratedCode(row, formate),
                        infraAnzeigeCode(row, formate),
                        infraKlartext(row, formate)
                    )
                },
                editable = false,
                allowCreate = false,
                allowSave = false,
                allowDelete = false,
                allowImportButton = false,
                allowExportButton = false,
                allowSettingsButton = false,
                nonEditableCols = infraCodeHeaders.toSet(),
                onCommit = { }
            )
        }
    }
}
